import { Experience } from "@ninetailed/experience.js-react";
import { ExperienceMapper } from "@ninetailed/experience.js-utils-contentful";
import { db } from "@utils/IndexedDb";
import cn from "@utils/cn";
import { getLink } from "@utils/linkUtils";
import { useLiveQuery } from "dexie-react-hooks";
import { AnimatePresence, motion } from "framer-motion";
import { useLocale, useTranslations } from "next-intl";
import dynamic from "next/dynamic";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { FC, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import FocusLock from "react-focus-lock";

import WishlistIcon from "@/components/WhishlistIcon/WhishlistIcon";
import AnchorLink from "@/components/ui/AnchorLink/AnchorLink";
import Button, { ButtonSize, ButtonTheme, ButtonVariant } from "@/components/ui/Button/Button";
import Icon from "@/components/ui/Icon/Icon";
import Image from "@/components/ui/Image/Image";
import { NavigationContext } from "@/context/NavigationContext";
import { TemplateContext } from "@/context/TemplateContext";
import { UserContext } from "@/context/UserContext";
import {
  browsePropertiesHeaderLinkClick,
  ctaClickTracking,
  navigationClickTracking,
  projectPageViewEvent,
  pushImmersiveExperience,
  searchInteractionTracking,
} from "@/dataLayer/dataLayer";
import useMediaQuery from "@/hooks/useMediaQuery";
import useScroll from "@/hooks/useScroll";
import { parseCustomExperience } from "@/lib/experiences";
import { HeaderProps, PRODUCT_TYPE, VISUAL_VARIANT } from "@/types";

// Dynamic import for ExperienceWrapper
const ExperienceWrapper = dynamic(() => import("../ExperienceWrapperProps"), {
  ssr: false,
  loading: () => <div>Loading...</div>,
});

const SoftLogin = dynamic(() => import("@/components/SoftLogin/SoftLogin"), { ssr: false });
const FilterBar = dynamic(() => import("@/components/ui/Form/Filters/FilterBar"), { ssr: false });
const ContactChannels = dynamic(() => import("@/components/ContactChannels/ContactChannels"), { ssr: false });

export const HEADER_DESKTOP = "header-desktop";

const whiteMenu = {
  darkLogo: "block",
  lightLogo: "hidden",
  iconStroke: "stroke-black",
  hamburgerVariant: "dark",
  textColor: "text-black",
  bgColor: "bg-white shadow-strong",
};

const whiteMenuNoShadow = {
  darkLogo: "block",
  lightLogo: "hidden",
  iconStroke: "stroke-black",
  hamburgerVariant: "dark",
  textColor: "text-black",
  bgColor: "bg-white border border-b-gray-10",
};

const transparentMenu = {
  darkLogo: "hidden",
  lightLogo: "block",
  iconStroke: "stroke-white",
  hamburgerVariant: "light",
  textColor: "text-white",
  bgColor: "bg-transparent shadow-none",
};

const currencyAndMeasurement: any = {
  currency: {
    AED: {
      en: "AED",
      ar: "درهم",
      ru: "дирхам",
      zh: "阿联酋迪拉姆",
      de: "AED",
      fr: "DEA",
      tr: "AED",
    },
    USD: {
      en: "USD",
      ar: "دولار أمريكي",
      ru: "доллар США",
      zh: "美元",
      de: "USD",
      fr: "USD",
      tr: "USD",
    },
    CNY: {
      en: "CNY",
      ar: "китайский юань",
      ru: "Yuan chinois",
      zh: "人民币",
      de: "CNY",
      fr: "CNY",
      tr: "CNY",
    },
    RUB: {
      en: "RUB",
      ar: "روبل روسي",
      ru: "Rouble russe",
      zh: "卢布",
      de: "RUB",
      fr: "FROTTER",
      tr: "RUB",
    },
    EUR: {
      en: "EUR",
      ar: "فرك",
      ru: "руб",
      zh: "欧元",
      de: "EUR",
      fr: "EUR",
      tr: "EUR",
    },
    GBP: {
      en: "GBP",
      ar: "جنيه إسترليني",
      ru: "Фунт стерлингов",
      zh: "英镑",
      de: "GBP",
      fr: "GBP",
      tr: "GBP",
    },
    INR: {
      en: "INR",
      ar: "روبية هندية",
      ru: "индийская рупия",
      zh: "印度卢比",
      de: "INR",
      fr: "INR",
      tr: "INR",
    },
  },
  measurement: {
    imperial: {
      en: "Imperial",
      ar: "النظام الإمبراطوري",
      ru: "имперский",
      zh: "平方英尺",
      de: "Kaiserlich",
      fr: "Impériale",
      tr: "İmperyal",
    },
    metric: {
      en: "Metric",
      ar: "النظام المتري",
      ru: "метрика",
      zh: "Metric",
      de: "Metrisch",
      fr: "Métrique",
      tr: "Metrik",
    },
  },
};

// Parse nt_experiences for a menu item
// const parseMenuItemExperiences = (item: any) => {
//   if (item?.nt_experiences && Array.isArray(item.nt_experiences)) {
//     console.log("Raw nt_experiences for item:", item.id, item.nt_experiences);
//     // Transform experiences to match Contentful structure
//     const transformedExperiences = item.nt_experiences.map((experience: any) => ({
//       sys: {
//         id: experience.id,
//         type: "Entry",
//         contentType: {
//           sys: {
//             id: "nt_experience",
//             type: "ContentType",
//             linkType: "ContentType",
//           },
//         },
//       },
//       fields: {
//         nt_name: experience.nt_name,
//         nt_description: experience.nt_description,
//         nt_type: experience.nt_type,
//         nt_config: experience.nt_config,
//         nt_audience: experience.nt_audience
//           ? {
//               sys: {
//                 id: experience.nt_audience.nt_audience_id || experience.nt_audience.id,
//                 type: "Entry",
//                 contentType: {
//                   sys: {
//                     id: "nt_audience",
//                     type: "ContentType",
//                     linkType: "ContentType",
//                   },
//                 },
//               },
//               fields: {
//                 nt_name: experience.nt_audience.nt_name,
//                 nt_rules: experience.nt_audience.nt_rules,
//                 nt_audience_id: experience.nt_audience.nt_audience_id,
//                 nt_metadata: experience.nt_audience.nt_metadata,
//               },
//             }
//           : undefined,
//         nt_variants: experience.nt_variants
//           ? experience.nt_variants.map((variant: any) => ({
//               sys: {
//                 id: variant.id,
//                 type: "Entry",
//                 contentType: {
//                   sys: {
//                     id: variant.type || "menuItem",
//                     type: "ContentType",
//                     linkType: "ContentType",
//                   },
//                 },
//               },
//               fields: {
//                 ...variant,
//               },
//             }))
//           : undefined,
//         nt_experience_id: experience.nt_experience_id,
//         nt_metadata: experience.nt_metadata,
//       },
//     }));
//     console.log("Transformed experiences for item:", item.id, transformedExperiences);
//     const mappedExperiences = transformedExperiences
//       .map((experience: any) => {
//         try {
//           return ExperienceMapper.mapExperience(experience);
//         } catch (error) {
//           console.error("Error mapping experience:", experience, error);
//           // Fallback to raw experience if mapping fails
//           return {
//             id: experience.sys.id,
//             name: experience.fields.nt_name,
//             type: experience.fields.nt_type,
//             config: experience.fields.nt_config,
//             audience: experience.fields.nt_audience
//               ? {
//                   id: experience.fields.nt_audience.sys.id,
//                   name: experience.fields.nt_audience.fields.nt_name,
//                   rules: experience.fields.nt_audience.fields.nt_rules,
//                 }
//               : undefined,
//             variants: experience.fields.nt_variants
//               ? experience.fields.nt_variants.map((variant: any) => ({
//                   id: variant.sys.id,
//                   ...variant.fields,
//                 }))
//               : [],
//             experienceId: experience.fields.nt_experience_id,
//             metadata: experience.fields.nt_metadata,
//           };
//         }
//       })
//       .filter((exp: any) => exp !== null);
//     console.log("Parsed experiences for item:", item.id, mappedExperiences);
//     return mappedExperiences;
//   }
//   console.log("No nt_experiences for item:", item.id);
//   return [];
// };

const panelVariation = {
  open: { opacity: 1, display: "block" },
  close: { opacity: 0, transitionEnd: { display: "none" } },
};
const transition = {
  duration: 0.3,
};

// If a Ninetailed variant is personalised, use it; otherwise use the baseline/variant fields passed in
const resolveActiveItem = (currentEntry: any, item: any) => {
  let activeItem = currentEntry?.fields || currentEntry || item;
  if (currentEntry?.nt_variants && currentEntry.nt_variants.length > 0 && currentEntry?.ninetailed?.isPersonalized) {
    activeItem = currentEntry.nt_variants[0];
  }
  return activeItem;
};

// Menu button and panel are defined at module level so their identity is stable across header renders.
// Declaring them inside the .map() made React unmount/remount every button and panel on each render (e.g. on scroll).
const DesktopMenuButton = (props: any) => {
  const { baseItem: item, textColor } = props;
  const { desktopPanelID, setDesktopPanelID } = useContext(NavigationContext);
  const activeItem = resolveActiveItem(props, item);

  return (
    <Button
      className={`${desktopPanelID === item.id && "underline"} hover:underline underline-offset-4 font-secondary text-[14px] flex justify-between items-center ${textColor}`}
      variant={ButtonVariant.TERTIARY}
      size={ButtonSize.SMALL}
      theme={ButtonTheme.BLACK}
      type="button"
      onClick={() => {
        navigationClickTracking(item?.internalName || item?.title);
        if (desktopPanelID === item.id) {
          setDesktopPanelID("");
        } else {
          setDesktopPanelID(item?.id);
        }
        activeItem.internalName === "Immersive Experience" &&
          pushImmersiveExperience(activeItem?.title || "", activeItem?.title, "HeaderDesktop", window.location.href);
        ctaClickTracking(activeItem?.title || "", "HeaderDesktop", "HeaderDesktop");
      }}
    >
      <span>{activeItem.title}</span>
    </Button>
  );
};

const DesktopMenuPanel = (props: any) => {
  const { baseItem: item, panelIndex, highlightFirstCluster } = props;
  const t = useTranslations();
  const locale = useLocale();
  const { desktopPanelID, setDesktopPanelID } = useContext(NavigationContext);
  const active = desktopPanelID === item.id;
  const activeItem = resolveActiveItem(props, item);

  return (
    <motion.div
      id={`modal-panel-desktop-${item.id}-${panelIndex}`}
      initial="close"
      variants={panelVariation}
      animate={active ? "open" : "close"}
      transition={transition}
      className={`backdrop-blur-2xl pt-m absolute top-[100%] bg-transparent w-full h-[calc(100dvh-96px)] overflow-auto`}
    >
      <div
        onClick={() => {
          setDesktopPanelID("");
        }}
        className="absolute left-0 top-0 w-full h-full"
      ></div>
      <div className="container">
        <FocusLock disabled={!active}>
          <div className="bg-white p-xl pb-m rounded-2xl relative">
            <div className="flex gap-xs justify-between">
              {activeItem?.columns?.map((column: any, index: any) => {
                return (
                  <div key={`desktop-${column?.id}`} className={cn(index === 0 ? "grow" : "flex-1")}>
                    <p className="py-s text-xs uppercase text-gray-50 font-primary font-medium">{column?.title}</p>
                    <ul>
                      {column?.links?.map((link: any) => {
                        return (
                          <li key={`desktop-menu-link-${link.id}`}>
                            <Button
                              className={`${highlightFirstCluster && index === 0 ? "text-l py-s" : "text-[14px] py-xs"} w-full no-underline flex justify-between items-center font-secondary font-medium hover:underline`}
                              variant={ButtonVariant.TERTIARY}
                              size={ButtonSize.SMALL}
                              theme={ButtonTheme.BLACK}
                              href={getLink(link, locale)}
                              onClick={() => {
                                browsePropertiesHeaderLinkClick({ link_category: column?.title, link_name: link?.label }, item?.title || "");
                                if (column?.title === "Featured Projects" || column?.title === "Communities") {
                                  projectPageViewEvent(link?.label);
                                }
                              }}
                            >
                              {link?.label}
                            </Button>
                          </li>
                        );
                      })}
                      {column?.viewAll && (
                        <li>
                          <Button
                            className={`${highlightFirstCluster && index === 0 ? "text-l py-s" : "text-[14px] py-xs"} w-full no-underline flex justify-between items-center font-secondary font-medium hover:underline`}
                            variant={ButtonVariant.TERTIARY}
                            size={ButtonSize.SMALL}
                            theme={ButtonTheme.BLACK}
                            href={getLink(column.viewAll, locale)}
                            onClick={() =>
                              browsePropertiesHeaderLinkClick(
                                { link_category: column?.title, link_name: column?.viewAll?.label || "" },
                                item?.title || "",
                              )
                            }
                          >
                            {column?.viewAll?.label}
                          </Button>
                        </li>
                      )}
                    </ul>
                  </div>
                );
              })}
            </div>
            {activeItem?.menuItemCta?.link && (
              <>
                <hr className="my-xl border-white border-b border-b-gray-10" />
                <div className="text-center">
                  <Button
                    href={getLink(activeItem?.menuItemCta, locale)}
                    customClass="block w-fit mx-auto"
                    variant={ButtonVariant.SECONDARY}
                    size={ButtonSize.MEDIUM}
                    theme={ButtonTheme.BLACK}
                    type="button"
                  >
                    {activeItem.menuItemCta.label}
                  </Button>
                </div>
              </>
            )}
            <button
              className="absolute right-m top-m"
              onClick={() => {
                setDesktopPanelID("");
              }}
            >
              <Icon name="close" className="stroke-black" size="24" title={t("label.common.close")} />
            </button>
          </div>
        </FocusLock>
      </div>
    </motion.div>
  );
};

const HeaderDesktop: FC<HeaderProps> = ({ logoDark, logoLight, menu, personalArea, contactChannels, onLogin, search, settings }) => {
  const scroll = useScroll();
  const t = useTranslations();
  const locale = useLocale();
  const desktopHeaderRef = useRef<HTMLDivElement>(null);
  const { fixedHeader } = useContext(TemplateContext);
  const { language, currency, measurement, isLoggedIn, isInventoryAvailable } = useContext(UserContext);
  const [isSo, setIsSo] = useState<boolean>(false);
  const isDesktop = useMediaQuery("(min-width: 1024px)");
  const [wasRedirected, setWasRedirected] = useState(false);
  const [isProjectPage, setIsProjectPage] = useState(false);
  const [isInvertyAvaliable, setIsInvertyAvaliable] = useState(false);
  const propertyTypeRecords: number | undefined = useLiveQuery(async () => {
    const projectCount = await db?.PROPERTY_TYPE?.count();
    return projectCount || 0;
  });
  const router = useRouter();
  const pathname = usePathname();
  const {
    desktopPanelID,
    setDesktopPanelID,
    setSettingsVisible,
    setLanguagesVisible,
    languagesVisible,
    settingsVisible,
    transitionComplete,
    setDesktopHeaderHeight,
  } = useContext(NavigationContext);

  const scrollClasses: Record<string, string> = useMemo(() => {
    if (!fixedHeader) {
      return pathname?.includes("search") ? whiteMenuNoShadow : whiteMenu;
    } else {
      if (scroll === 0) {
        if (desktopPanelID !== "") {
          return pathname?.includes("search") ? whiteMenuNoShadow : whiteMenu;
        } else {
          return pathname?.includes("terms-and-conditions") || pathname?.includes("privacy-policy") ? whiteMenu : transparentMenu;
        }
      } else {
        return pathname?.includes("search") ? whiteMenuNoShadow : whiteMenu;
      }
    }
  }, [scroll, desktopPanelID, fixedHeader, pathname]);
  useEffect(() => {
    if (typeof window !== "undefined") {
      const redirected = localStorage.getItem("redirected");
      const redirectedProject = localStorage.getItem("redirectedProject");
      const path = pathname.split("/").filter(Boolean).pop();
      if (path === redirectedProject) {
        setWasRedirected(redirected === "true");
      } else {
        // user navigated away from the redirected project page, clear the stored flags
        setWasRedirected(false);
        localStorage.removeItem("redirected");
        localStorage.removeItem("redirectedProject");
      }
    }
  }, [pathname]);

  const keyboardHandler = useCallback(
    (event: KeyboardEvent) => {
      if (desktopPanelID !== "" && event.key.toLowerCase() === "escape") {
        setDesktopPanelID("");
      }
    },
    [desktopPanelID, setDesktopPanelID],
  );

  useEffect(() => {
    if (document) {
      document.addEventListener("keydown", keyboardHandler);
      return () => {
        document.removeEventListener("keydown", keyboardHandler);
      };
    }
  }, [keyboardHandler]);

  const languageCallback = useCallback(() => {
    if (settingsVisible) {
      setLanguagesVisible(languagesVisible);
    }
    if (desktopPanelID) {
      setDesktopPanelID("");
    }
    setLanguagesVisible(!languagesVisible);
  }, [settingsVisible, languagesVisible, desktopPanelID, setDesktopPanelID, setLanguagesVisible]);

  const settingsCallback = useCallback(() => {
    if (languagesVisible) {
      setLanguagesVisible(settingsVisible);
    }
    if (desktopPanelID) {
      setDesktopPanelID("");
    }
    setSettingsVisible(!settingsVisible);
  }, [settingsVisible, languagesVisible, desktopPanelID, setDesktopPanelID, setLanguagesVisible, setSettingsVisible]);

  useEffect(() => {
    if (desktopHeaderRef.current) {
      setDesktopHeaderHeight(desktopHeaderRef.current.clientHeight);
    }
  }, [desktopHeaderRef, setDesktopHeaderHeight]);

  // Parsed once per menu, not on every header render (header re-renders on scroll)
  const menuExperiences = useMemo(() => menu?.items?.map((item) => parseCustomExperience(item)) ?? [], [menu?.items]);

  const loginHandler = (success: boolean, otpError?: boolean) => onLogin && onLogin(success, otpError);

  useEffect(() => {
    if (typeof window !== "undefined") {
      const value = localStorage?.getItem("isSo");
      if (value) {
        setIsSo(value === "true");
      }
    }
  }, []);
  useEffect(() => {
    const path = pathname.split("/").filter(Boolean).pop();
    const ProjectPage = pathname.split("/").filter(Boolean).at(-2);
    ProjectPage ? setIsProjectPage(ProjectPage === "projects") : setIsProjectPage(false);
    path === isInventoryAvailable.slug && setIsInvertyAvaliable(isInventoryAvailable.available);
  }, [isInventoryAvailable, pathname]);

  return (
    <div
      ref={desktopHeaderRef}
      id={HEADER_DESKTOP}
      className={`${scrollClasses?.bgColor} ${fixedHeader ? "fixed" : "sticky"} transition-all w-full top-0 hidden md:block z-50`}
    >
      {/* Back header */}
      <div className="bg-black relative h-[28px] flex items-center">
        <div className={`flex container py-xxs ${isSo ? "justify-center" : "justify-between"}`}>
          <div className="flex items-center gap-s">
            <AnimatePresence>
              {settings?.settings?.languages && settings?.settings?.languages?.length > 1 && (
                <motion.button initial={{ opacity: 0 }} animate={{ opacity: 1 }} onClick={languageCallback} disabled={!transitionComplete}>
                  <div className="flex items-center text-white">
                    <span className="font-secondary text-s">
                      {t("header.label.languages")}: {language.toUpperCase()}
                    </span>
                    {/* <Icon size="16" className="stroke-white" name="caret-down" /> */}
                  </div>
                </motion.button>
              )}
            </AnimatePresence>
            <AnimatePresence>
              {currency && measurement && (
                <motion.button initial={{ opacity: 0 }} animate={{ opacity: 1 }} onClick={settingsCallback} disabled={!transitionComplete}>
                  <div className="flex items-center text-white">
                    <span className="font-secondary text-s">
                      {t("header.label.settings")}: {currencyAndMeasurement.currency[currency]?.[locale?.split("-")[0]]} /
                      {currencyAndMeasurement.measurement[measurement]?.[locale?.split("-")[0]]}
                    </span>
                  </div>
                </motion.button>
              )}
            </AnimatePresence>
          </div>
          {!isSo && (
            <ContactChannels
              desktop={true}
              contactChannels={contactChannels}
              customClass="text-white font-secondary text-s"
              variation="stroke-white"
            />
          )}
        </div>
      </div>
      {/* Back header */}
      {/* Main header */}
      <div className="backdrop-blur-2xl">
        <div className={`container flex justify-between py-l h-[60px]`}>
          <div className="flex items-center gap-l" style={isSo ? { marginLeft: "auto", marginRight: "auto" } : {}}>
            <AnchorLink className="block max-w-[133px] md:max-w-[114px] max-h-[30px]" href={`/${locale}/`} aria-label={t("button.label.homepage")}>
              <Image
                fit="pad"
                loading="eager"
                className={`block w-auto ${scrollClasses.darkLogo}`}
                src={logoDark?.url}
                width={114}
                height={30}
                alt={logoDark?.title}
              />
              <Image
                fit="pad"
                loading="eager"
                className={`block w-auto ${scrollClasses.lightLogo}`}
                src={logoLight?.url}
                width={114}
                height={30}
                alt={logoDark?.title}
              />
            </AnchorLink>
            {!isSo && (
              <nav className="flex gap-l">
                {menu?.items?.map((item, index) => {
                  const parsedExperiences = menuExperiences[index] || [];
                  const buttonProps = { baseItem: item, textColor: scrollClasses.textColor };

                  return (
                    <div key={`${item.id}-desktop-${index}`}>
                      {parsedExperiences.length > 0 ? (
                        <Experience
                          {...item}
                          key={`experience-button-${item.id}`}
                          id={item?.id as any}
                          component={DesktopMenuButton as any}
                          experiences={parsedExperiences}
                          passthroughProps={buttonProps as any}
                        />
                      ) : (
                        <DesktopMenuButton {...item} {...buttonProps} key={`experience-button-${item.id}`} />
                      )}
                    </div>
                  );
                })}
              </nav>
            )}
          </div>
          {!isSo && (
            <nav className="flex gap-l items-center">
              <Link
                href={getLink("search", locale)}
                onClick={(e) => {
                  e.preventDefault();
                  setDesktopPanelID("search");
                  searchInteractionTracking();
                }}
                className={`flex items-center text-[14px] ${scrollClasses?.textColor}`}
                aria-label={t("button.label.search")}
              >
                <span className="mr-s whitespace-nowrap">{t("button.label.search")}</span>
                <Icon name="search" className={scrollClasses?.iconStroke} size="24" title={t("button.label.search")} />
              </Link>
              {personalArea && personalArea?.link && (
                <AnchorLink className="hidden cursor-pointer" href={isLoggedIn ? personalArea?.link?.url : ""} span={!isLoggedIn}>
                  <div className="flex flex-col items-center">
                    <SoftLogin
                      iconClass={scrollClasses.iconStroke}
                      icon={{ notLoggedIcon: "user-unselected", loggedIcon: "user-selected" }}
                      onLogin={loginHandler}
                    />
                  </div>
                </AnchorLink>
              )}
              <div className="flex flex-col items-center">
                <WishlistIcon iconClass={scrollClasses?.iconStroke} textClass={scrollClasses?.textColor} />
              </div>
            </nav>
          )}
        </div>
      </div>

      {isProjectPage &&
        wasRedirected &&
        (isInvertyAvaliable ? (
          <></>
        ) : (
          <div className="flex justify-center items-center bg-gray-10 cursor-pointer">
            <span className="text-[12px] font-secondary font-medium p-[10px]">
              {t("title.label.NoPropertyTypeAvailable")}
              <Button
                className={`text-[12px] font-secondary font-medium pb-[8px] pl-[8px] underline cursor-pointer`}
                variant={ButtonVariant.TERTIARY}
                size={ButtonSize.SMALL}
                theme={ButtonTheme.BLACK}
                // href={props?.link?.id}
                onClick={() => {
                  router.push(`/${locale}/search`);
                }}
              >
                {t("button.label.searchAnotherUnit")}
              </Button>
            </span>
          </div>
        ))}

      {!isSo && (
        <>
          {menu?.items?.map((item, index) => {
            const parsedExperiences = menuExperiences[index] || [];
            const panelProps = { baseItem: item, panelIndex: index, highlightFirstCluster: menu?.highlightFirstCluster };

            return parsedExperiences.length > 0 ? (
              <Experience
                {...item}
                key={`experience-${item.id}`}
                id={item?.id as any}
                component={DesktopMenuPanel as any}
                experiences={parsedExperiences}
                passthroughProps={panelProps as any}
              />
            ) : (
              <DesktopMenuPanel {...item} {...panelProps} key={`experience-${item.id}`} />
            );
          })}
        </>
      )}
      {/* Navigation panels */}
      {/* Search panel */}
      <AnimatePresence>
        {desktopPanelID === "search" && (
          <motion.div
            id={`modal-panel-desktop-search`}
            initial={{ opacity: 0 }}
            exit={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            key={`subpanel-desktop-search`}
            className={`backdrop-blur-2xl pt-m fixed left-0 top-0 bg-transparent w-full h-screen overflow-auto z-50`}
          >
            <FocusLock>
              <div onClick={() => setDesktopPanelID("")} className="absolute left-0 top-0 w-full h-full"></div>
              <div className="container">
                <div className="text-right invert">
                  <button onClick={() => setDesktopPanelID("")}>
                    <Icon name="close" size="32" className="stroke-black" />
                  </button>
                </div>
                <div className="md:mt-4xl">
                  <FilterBar name={HEADER_DESKTOP} filters={[PRODUCT_TYPE.PROPERTY_TYPE, "bedrooms", "price"]} variant={VISUAL_VARIANT.LIGHT} />
                </div>
              </div>
            </FocusLock>
          </motion.div>
        )}
      </AnimatePresence>
      {/* Search panel */}
    </div>
  );
};

export default HeaderDesktop;
